// src/utils/slugUtils.js
// Utility functions for business website slug management

import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

// Reserved slugs that cannot be used
const RESERVED_SLUGS = [
  "admin", "api", "app", "www", "mail", "ftp",
  "dashboard", "login", "signup", "support", "help",
  "billing", "account", "settings", "demo", "test",
  "staging", "dev", "production", "status", "pricing",
  "employee", "employees", "customer", "customers",
  "invoice", "invoices", "estimate", "estimates",
  "booking", "bookings", "request", "requests",
  "b", "business", "profile", "public"
];

// Slug validation rules
const SLUG_MIN_LENGTH = 3;
const SLUG_MAX_LENGTH = 50;
const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;  // Letters, numbers, and hyphens (not at start/end)

/**
 * Generate a URL-friendly slug from a business name
 * @param {string} businessName - The business name to convert
 * @returns {string} - URL-friendly slug
 *
 * Examples:
 * - "Premier Shine" → "premiershine"
 * - "Bob's Cleaning Service" → "bobscleaningservice"
 */
export function generateSlug(businessName) {
  if (!businessName) return "";

  return businessName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]/g, "")     // Remove everything except letters and numbers
    .substring(0, SLUG_MAX_LENGTH);
}

/**
 * Validate a slug against rules
 * @param {string} slug - The slug to validate
 * @returns {{ isValid: boolean, errors: string[] }}
 */
export function validateSlug(slug) {
  const errors = [];

  if (!slug) {
    errors.push("Slug is required");
    return { isValid: false, errors };
  }

  const normalizedSlug = slug.toLowerCase().trim();

  if (normalizedSlug.length < SLUG_MIN_LENGTH) {
    errors.push(`URL must be at least ${SLUG_MIN_LENGTH} characters`);
  }

  if (normalizedSlug.length > SLUG_MAX_LENGTH) {
    errors.push(`URL cannot exceed ${SLUG_MAX_LENGTH} characters`);
  }

  if (!SLUG_PATTERN.test(normalizedSlug)) {
    errors.push("URL can only contain lowercase letters and numbers");
  }

  if (RESERVED_SLUGS.includes(normalizedSlug)) {
    errors.push("This URL is reserved and cannot be used");
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Check if a slug is available (not already taken)
 * @param {string} slug - The slug to check
 * @param {string} [currentBusinessId] - Current business ID (to allow keeping same slug)
 * @returns {Promise<boolean>}
 */
export async function isSlugAvailable(slug, currentBusinessId = null) {
  if (!slug) return false;

  const normalizedSlug = slug.toLowerCase().trim();

  // Check validation first
  const validation = validateSlug(normalizedSlug);
  if (!validation.isValid) return false;

  try {
    const slugDoc = await getDoc(doc(db, "slugs", normalizedSlug));

    if (!slugDoc.exists()) return true;

    // If the slug belongs to the current business, it's available
    if (currentBusinessId && slugDoc.data().businessId === currentBusinessId) {
      return true;
    }

    return false;
  } catch (error) {
    console.error("Error checking slug availability:", error);
    return false;
  }
}

/**
 * Generate a unique slug, appending numbers if necessary
 * @param {string} businessName - The business name
 * @returns {Promise<string>} - A unique, available slug
 */
export async function generateUniqueSlug(businessName) {
  const baseSlug = generateSlug(businessName);

  if (!baseSlug) return "";

  // Check if base slug is available
  if (await isSlugAvailable(baseSlug)) {
    return baseSlug;
  }

  // Try adding numbers until we find an available one
  let counter = 2;
  const maxAttempts = 100;

  while (counter <= maxAttempts) {
    const newSlug = `${baseSlug}${counter}`.substring(0, SLUG_MAX_LENGTH);
    if (await isSlugAvailable(newSlug)) {
      return newSlug;
    }
    counter++;
  }

  // Fallback: append random alphanumeric suffix
  const randomSuffix = Math.random().toString(36).substring(2, 8);
  return `${baseSlug.substring(0, SLUG_MAX_LENGTH - randomSuffix.length)}${randomSuffix}`;
}

/**
 * Register a slug for a business (creates the slug document)
 * @param {string} slug - The slug to register
 * @param {string} businessId - The business ID
 * @returns {Promise<boolean>} - Success status
 */
export async function registerSlug(slug, businessId) {
  if (!slug || !businessId) return false;

  const normalizedSlug = slug.toLowerCase().trim();

  try {
    await setDoc(doc(db, "slugs", normalizedSlug), {
      businessId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error("Error registering slug:", error);
    return false;
  }
}

/**
 * Build the public website URL for a business
 * @param {string} slug - The business slug
 * @returns {string} - Full URL to the business website
 */
export function buildBusinessUrl(slug) {
  if (!slug) return "";

  // Use the current origin in development, or the production domain
  const baseUrl = process.env.NODE_ENV === "production"
    ? "https://effit-trak.com"
    : window.location.origin;

  return `${baseUrl}/b/${slug}`;
}

/**
 * Get the default public profile configuration
 * @returns {object} - Default publicProfile object
 */
export function getDefaultPublicProfile(slug) {
  return {
    enabled: true,
    slug: slug || "",
    headline: "",
    description: "",
    coverImage: "",
    businessHours: {
      monday: { open: "09:00", close: "17:00", closed: false },
      tuesday: { open: "09:00", close: "17:00", closed: false },
      wednesday: { open: "09:00", close: "17:00", closed: false },
      thursday: { open: "09:00", close: "17:00", closed: false },
      friday: { open: "09:00", close: "17:00", closed: false },
      saturday: { open: "10:00", close: "14:00", closed: false },
      sunday: { open: "", close: "", closed: true }
    },
    featuredServices: [],
    showAllServices: true,
    socialLinks: {
      facebook: "",
      instagram: "",
      twitter: "",
      linkedin: "",
      website: ""
    },
    googleReviewUrl: "",
    viewCount: 0
    // blocks is deliberately absent here - normalizeBlocks(undefined) generates
    // fresh DEFAULT_BLOCKS() so every code path (including brand-new signups)
    // goes through the same block-builder default, see src/utils/websiteBlocks.js
  };
}
